import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import {
  Mail, Shield, FolderOpen, Briefcase, User as UserIcon,
  Database, HardDrive, Calendar, TrendingUp, TrendingDown,
  Tag, Wallet, Layers, ListChecks, FileClock,
} from 'lucide-react';
import api from '../lib/api';
import { formatBRL, formatDateBR } from '../lib/format';

function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const COUNT_LABELS = {
  transactions: { label: 'Transações', icon: ListChecks },
  payable_receivable: { label: 'Contas P/R', icon: FileClock },
  accounts: { label: 'Contas', icon: Wallet },
  pots: { label: 'Potes', icon: Layers },
  categories: { label: 'Categorias', icon: Tag },
  entities: { label: 'Entidades', icon: UserIcon },
  revenue_sources: { label: 'Fontes de Receita', icon: TrendingUp },
  tags: { label: 'Tags', icon: Tag },
};

export default function Profile() {
  const { user } = useAuth();
  const { projects, currentProject } = useProject();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/profile/stats');
        if (alive) setStats(r.data);
      } catch (e) {
        console.error('profile stats error', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!user) return null;

  const totalTx = stats?.totals?.transactions ?? 0;
  const totalPR = stats?.totals?.payable_receivable ?? 0;

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">Perfil</h2>

      {/* User card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.email} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-indigo-100 dark:border-indigo-900" />
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
              {user.email?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 break-words" data-testid="profile-name">{user.name || user.email}</div>
            <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-1 break-all"><Mail className="w-4 h-4 shrink-0" /> {user.email}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${user.role==='admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                <Shield className="w-3 h-3" />
                {user.role === 'admin' ? 'Administrador' : 'Usuário'}
              </span>
              {stats?.member_since && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Desde {formatDateBR(stats.member_since)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Storage & totals summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={HardDrive}
          label="Armazenamento"
          value={loading ? '—' : formatBytes(stats?.storage_bytes)}
          hint={loading ? '' : `${stats?.storage_mb?.toFixed(3) ?? 0} MB`}
          color="from-blue-500 to-indigo-500"
          testId="stat-storage"
        />
        <SummaryCard
          icon={FolderOpen}
          label="Projetos"
          value={projects.length}
          color="from-indigo-500 to-purple-500"
          testId="stat-projects"
        />
        <SummaryCard
          icon={ListChecks}
          label="Transações"
          value={loading ? '—' : totalTx.toLocaleString('pt-BR')}
          color="from-emerald-500 to-teal-500"
          testId="stat-transactions"
        />
        <SummaryCard
          icon={FileClock}
          label="Contas P/R"
          value={loading ? '—' : totalPR.toLocaleString('pt-BR')}
          color="from-amber-500 to-orange-500"
          testId="stat-pr"
        />
      </div>

      {/* Per-project details */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Detalhes por Projeto
        </h3>

        {loading && <div className="text-sm text-slate-500 dark:text-slate-400 py-4">Carregando...</div>}

        {!loading && (
          <div className="space-y-4">
            {(stats?.projects || []).map((p) => {
              const Icon = p.type === 'business' ? Briefcase : UserIcon;
              const income = p.income_all_time || 0;
              const expense = p.expense_all_time || 0;
              return (
                <div key={p.id} data-testid={`project-stats-${p.id}`} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:border-indigo-300 dark:hover:border-indigo-600 transition">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: p.color || '#4f46e5' }}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {p.type === 'business' ? 'Empresa' : 'Pessoal'} · {formatBytes(p.storage_bytes)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <span className="text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" /> {formatBRL(income)}
                      </span>
                      <span className="text-rose-700 dark:text-rose-400 flex items-center gap-1">
                        <TrendingDown className="w-3.5 h-3.5" /> {formatBRL(expense)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-2">
                    {Object.entries(COUNT_LABELS).map(([key, cfg]) => {
                      const CIcon = cfg.icon;
                      const v = p.counts?.[key] ?? 0;
                      return (
                        <div key={key} className="bg-slate-50 dark:bg-slate-700/40 rounded-lg px-2 py-2 text-center">
                          <CIcon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 mx-auto mb-1" />
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{v.toLocaleString('pt-BR')}</div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">{cfg.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {(p.first_transaction || p.last_transaction) && (
                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-4 flex-wrap">
                      {p.first_transaction && <span>Primeira: {formatDateBR(p.first_transaction)}</span>}
                      {p.last_transaction && <span>Última: {formatDateBR(p.last_transaction)}</span>}
                    </div>
                  )}
                </div>
              );
            })}
            {(stats?.projects || []).length === 0 && (
              <div className="text-sm text-slate-500 py-4">Nenhum projeto encontrado.</div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, hint, color, testId }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
          <div className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{value}</div>
          {hint && <div className="text-[10px] text-slate-400 dark:text-slate-500">{hint}</div>}
        </div>
      </div>
    </div>
  );
}
